import { PaginationState } from '@tanstack/react-table';
import { Dispatch, SetStateAction, useCallback, useLayoutEffect, useRef, useState } from 'react';

export const DEFAULT_GRID_PAGE_SIZE = 25;

export interface UseGridPaginationResult {
  pagination: PaginationState;
  setPagination: Dispatch<SetStateAction<PaginationState>>;
  revealRow: (rowId: string | number) => void;
  resetToFirstPage: () => void;
  /** Hand to the table as `scrollToRowId` so the revealed row is scrolled to, not just paged to. */
  scrollToRowId: string | number | null;
}

/**
 * Owns the page index of a client-side paginated grid whose rows are edited in place.
 *
 * Such a grid has to opt out of TanStack's `autoResetPageIndex`, because that fires on every change of the
 * data array's identity — which for an editable grid means every keystroke, throwing the user back to page
 * one mid-edit. Opting out means the two cases the auto-reset legitimately covered have to be handled here:
 *
 * - `revealRow` moves to whichever page a row landed on, so a row added outside the page being viewed is
 *   actually shown once it is added. It also reports that row as `scrollToRowId`, since the page it is
 *   on holds more rows than the grid shows at once.
 * - Rows can disappear (a delete, a narrower search term) and strand the view past the end of the list, so
 *   the page index is clamped back onto the last page that still holds rows.
 *
 * `rows` must be the same array handed to the table, filtering included: page numbers are positions in it.
 */
export function useGridPagination<T>(
  rows: T[],
  getRowId: (row: T) => string | number,
  initialPageSize: number = DEFAULT_GRID_PAGE_SIZE,
): UseGridPaginationResult {
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: initialPageSize });
  // Held apart on purpose. The page jump is consumed once, or every later edit would drag the user back to
  // the revealed row's page; the id handed to the table stays put, and the table itself acts on it once.
  const rowToReveal = useRef<string | number | null>(null);
  const [scrollToRowId, setScrollToRowId] = useState<string | number | null>(null);
  // Read through a ref so the effect below reacts to the rows alone. Callers hand over the same accessor
  // they give the table, which is typically an inline `row => row.id` and changes identity every render.
  const getRowIdRef = useRef(getRowId);
  getRowIdRef.current = getRowId;

  const revealRow = useCallback((rowId: string | number) => {
    rowToReveal.current = rowId;
    setScrollToRowId(rowId);
  }, []);

  const resetToFirstPage = useCallback(() => {
    setPagination(prev => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
  }, []);

  // Layout effect: this corrects which page is on screen, so it has to land before the browser paints.
  // A passive effect would let a stale or out-of-range page flash first.
  useLayoutEffect(() => {
    const pendingRowId = rowToReveal.current;
    rowToReveal.current = null;

    if (pendingRowId !== null) {
      const index = rows.findIndex(row => getRowIdRef.current(row) === pendingRowId);
      // A row that is not in the list has nowhere to be revealed — it was filtered out by an active search.
      if (index >= 0) {
        setPagination(prev => {
          const pageIndex = Math.floor(index / prev.pageSize);
          return pageIndex === prev.pageIndex ? prev : { ...prev, pageIndex };
        });
        return;
      }
    }

    setPagination(prev => {
      const lastPageIndex = Math.max(0, Math.ceil(rows.length / prev.pageSize) - 1);
      return prev.pageIndex > lastPageIndex ? { ...prev, pageIndex: lastPageIndex } : prev;
    });
  }, [rows]);

  return { pagination, setPagination, revealRow, resetToFirstPage, scrollToRowId };
}
